import type { SimulationMesh } from '@/simulation/mesh';

/**
 * Binary STL export.
 *
 * Layout: 80-byte header, uint32 triangle count, then 50 bytes per triangle
 * (normal + three vertices as float32, plus a uint16 attribute word).
 *
 * STL has no notion of a two-sided surface, so the exported solid is a zero
 * thickness shell. Slicers will complain; that is a property of the format, not
 * a bug, and the UI says so at export time.
 */
export function meshToStl(
  mesh: SimulationMesh,
  positions: Float32Array | Float64Array,
): ArrayBuffer {
  const count = mesh.triangleCount;
  const buffer = new ArrayBuffer(84 + count * 50);
  const view = new DataView(buffer);
  const header = 'fold.app export';
  for (let i = 0; i < header.length && i < 80; i++) {
    view.setUint8(i, header.charCodeAt(i));
  }
  view.setUint32(80, count, true);

  let offset = 84;
  for (let t = 0; t < count; t++) {
    const a = mesh.triangles[t * 3];
    const b = mesh.triangles[t * 3 + 1];
    const c = mesh.triangles[t * 3 + 2];
    const ax = positions[a * 3], ay = positions[a * 3 + 1], az = positions[a * 3 + 2];
    const bx = positions[b * 3], by = positions[b * 3 + 1], bz = positions[b * 3 + 2];
    const cx = positions[c * 3], cy = positions[c * 3 + 1], cz = positions[c * 3 + 2];
    const ux = bx - ax, uy = by - ay, uz = bz - az;
    const vx = cx - ax, vy = cy - ay, vz = cz - az;
    let nx = uy * vz - uz * vy;
    let ny = uz * vx - ux * vz;
    let nz = ux * vy - uy * vx;
    const len = Math.hypot(nx, ny, nz) || 1;
    nx /= len;
    ny /= len;
    nz /= len;
    for (const value of [nx, ny, nz, ax, ay, az, bx, by, bz, cx, cy, cz]) {
      view.setFloat32(offset, Number.isFinite(value) ? value : 0, true);
      offset += 4;
    }
    view.setUint16(offset, 0, true);
    offset += 2;
  }
  return buffer;
}
